import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useViewerGoals from '../hooks/useViewerGoals';

const DEFAULT_CYCLE_MS = 6000;

export default function RotatingStatsPanel({ channelName, config, active = true }) {
  const { goalsData, loading, getSubGoal, getFollowerGoal } = useViewerGoals(channelName, active);
  const [currentIndex, setCurrentIndex] = useState(0);

  const statsConfig = config?.rotatingStats;
  const appearance = config?.appearance;
  const cycleMs = statsConfig?.cycleDurationMs || DEFAULT_CYCLE_MS;

  const enabledItems = useMemo(() => {
    if (!statsConfig?.items) return null;
    return statsConfig.items.filter(item => item.enabled);
  }, [statsConfig]);

  const labelMap = useMemo(() => {
    if (!statsConfig?.items) return {};
    const map = {};
    for (const item of statsConfig.items) {
      map[item.key] = item.label;
    }
    return map;
  }, [statsConfig]);

  const buildWidgets = useCallback(() => {
    const subGoal = getSubGoal();
    const followerGoal = getFollowerGoal();

    const allWidgets = [
      { key: 'lastSub', label: labelMap.lastSub || 'LAST SUB', value: goalsData?.lastSubscriber?.username || '\u2014' },
      { key: 'lastFollow', label: labelMap.lastFollow || 'LAST FOLLOW', value: goalsData?.lastFollower?.username || '\u2014' },
      {
        key: 'topCheer',
        label: labelMap.topCheer || 'TOP CHEER',
        value: goalsData?.lastCheerer?.username || '\u2014',
        extra: goalsData?.lastCheerer?.bits ? `${goalsData.lastCheerer.bits} bits` : null,
      },
      {
        key: 'checkInStreak',
        label: labelMap.checkInStreak || 'CHECK-IN STREAK',
        value: goalsData?.firstStreak?.username || '\u2014',
        extra: goalsData?.firstStreak?.streak ? `${goalsData.firstStreak.streak} streams` : null,
      },
      {
        key: 'subs',
        label: labelMap.subs || 'SUBS',
        value: subGoal ? `${subGoal.current}/${subGoal.target}` : '\u2014',
        progress: subGoal ? Math.min((subGoal.current / subGoal.target) * 100, 100) : null,
      },
      {
        key: 'followers',
        label: labelMap.followers || 'FOLLOWERS',
        value: followerGoal ? `${followerGoal.current}/${followerGoal.target}` : '\u2014',
        progress: followerGoal ? Math.min((followerGoal.current / followerGoal.target) * 100, 100) : null,
      },
      { key: 'firstToday', label: labelMap.firstToday || 'FIRST TODAY', value: goalsData?.firstThisStream?.username || 'no one yet!' },
    ];

    if (!enabledItems) return allWidgets;

    const enabledKeys = enabledItems.map(i => i.key);
    return enabledKeys
      .map(key => allWidgets.find(w => w.key === key))
      .filter(Boolean);
  }, [goalsData, getSubGoal, getFollowerGoal, labelMap, enabledItems]);

  const widgets = buildWidgets();
  const totalWidgets = widgets.length;

  useEffect(() => {
    if (loading || totalWidgets === 0) return;
    setCurrentIndex(prev => prev % totalWidgets);
    const id = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % totalWidgets);
    }, cycleMs);
    return () => clearInterval(id);
  }, [loading, totalWidgets, cycleMs]);

  if (loading) {
    return (
      <div className="rotating-panel">
        <div className="rotating-panel-loading">LOADING...</div>
        <style>{baseStyles}</style>
      </div>
    );
  }

  if (totalWidgets === 0) {
    return (
      <div className="rotating-panel">
        <div className="rotating-panel-loading">NO STATS ENABLED</div>
        <style>{baseStyles}</style>
      </div>
    );
  }

  const w = widgets[currentIndex % totalWidgets];

  const labelColor = appearance?.labelColor || '#d4c4a8';
  const valueColor = appearance?.valueColor || '#f5f0e6';
  const accentColor = appearance?.accentColor || '#e8b298';
  const progressBarColor = appearance?.progressBarColor || '#a65241';

  return (
    <div className="rotating-panel">
      <div key={currentIndex} className="rotating-single-widget widget-flicker">
        <span className="rotating-widget-label" style={{ color: labelColor }}>{w.label}</span>
        <span className="rotating-widget-value" style={{ color: valueColor, textShadow: `1px 1px 0 ${progressBarColor}` }}>{w.value}</span>
        {w.extra && <span className="rotating-widget-extra" style={{ color: accentColor }}>{w.extra}</span>}
        {w.progress != null && (
          <div className="rotating-progress-bar" style={{ borderColor: labelColor }}>
            <div
              className="rotating-progress-fill"
              style={{
                width: `${w.progress}%`,
                background: `linear-gradient(90deg, ${progressBarColor}, ${accentColor})`,
                boxShadow: `0 0 6px ${progressBarColor}99`
              }}
            />
          </div>
        )}
      </div>

      <div className="rotating-dots">
        {Array.from({ length: totalWidgets }).map((_, i) => (
          <span key={i} className={`rotating-dot${i === currentIndex % totalWidgets ? ' active' : ''}`} />
        ))}
      </div>

      <style>{baseStyles}</style>
    </div>
  );
}

const baseStyles = `
  .rotating-panel {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    font-family: 'Press Start 2P', monospace;
  }

  .rotating-panel-loading {
    font-size: 0.67rem;
    color: #d4c4a8;
    text-align: center;
  }

  .rotating-single-widget {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 1;
    min-height: 0;
  }

  .widget-flicker {
    animation: widgetFade 0.6s ease-in-out;
  }

  @keyframes widgetFade {
    0% { opacity: 0; transform: translateY(3px); }
    100% { opacity: 1; transform: translateY(0); }
  }

  .rotating-widget-label {
    font-size: 0.85rem;
    text-shadow: 1px 1px 0 #0d4a54;
    letter-spacing: 0.05em;
    line-height: 1.4;
    text-align: center;
    white-space: nowrap;
  }

  .rotating-widget-value {
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.4;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .rotating-widget-extra {
    font-size: 0.75rem;
    text-shadow: 1px 1px 0 #0d4a54;
    line-height: 1.4;
    text-align: center;
  }

  .rotating-progress-bar {
    width: 60%;
    height: 8px;
    background: #0d4a54;
    border: 2px solid #d4c4a8;
    overflow: hidden;
    margin-top: 2px;
  }

  .rotating-progress-fill {
    height: 100%;
    transition: width 0.5s ease;
  }

  .rotating-dots {
    display: flex;
    justify-content: center;
    gap: 5px;
    padding-top: 4px;
  }

  .rotating-dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(26, 110, 122, 0.4);
    transition: all 0.3s ease;
  }

  .rotating-dot.active {
    background: #1a6e7a;
    box-shadow: 0 0 4px rgba(26, 110, 122, 0.6);
  }
`;
